import { Component, computed, HostListener, inject, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { AuthService } from './core/services/auth/auth.service';
import { ThemeMode, ThemeService } from './core/services/theme/theme.service';
import { IconComponent } from './shared/components/icon/icon.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, IconComponent],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App implements OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private themeService = inject(ThemeService);
  breadcrumbs: { label: string; url: string }[] = [];
  private destroySubject$ = new Subject<void>();
  readonly currentUrl = signal(this.router.url);
  readonly userMenuOpen = signal(false);

  /** Current URL with any query string / fragment stripped, for nav matching. */
  private readonly activePath = computed(() => this.currentUrl().split(/[?#]/)[0]);

  // Must compare the query-stripped path: Okta returns from the PKCE flow to
  // /login/callback?code=…&state=…, so a raw URL comparison never matches and
  // the header would render over the callback screen.
  readonly showHeader = computed(() => {
    const path = this.activePath();
    return path !== '/login' && path !== '/login/callback';
  });

  /** Current theme selection, surfaced in the user-menu Appearance control. */
  readonly themeMode = this.themeService.mode;
  readonly themeOptions: { mode: ThemeMode; label: string }[] = [
    { mode: 'light', label: 'Light' },
    { mode: 'dark', label: 'Dark' },
    { mode: 'system', label: 'System' },
  ];

  // The request detail route (/request-detail/:id) is a sibling path of
  // /requests, so routerLinkActive can't match it. Treat the whole requests
  // area (table + detail) as one section so the Requests nav link stays
  // highlighted throughout. The proxy queue lives under /requests too but has
  // its own nav link, so it is excluded here to avoid two active tabs.
  readonly requestsSectionActive = computed(() => {
    const path = this.activePath();
    return (
      (path.startsWith('/requests') && !path.startsWith('/requests/proxy-verifications')) ||
      path.startsWith('/request-detail')
    );
  });

  /** Highlights the Proxy Queue nav link across the proxy-verifications route. */
  readonly proxyQueueSectionActive = computed(() =>
    this.activePath().startsWith('/requests/proxy-verifications'),
  );

  // Every audit-log drill-down is a sibling path of /activity-logs, so routerLinkActive can't
  // match them and the Audit Log link would unhighlight the moment a row is opened. Treat the
  // list and its three detail screens as one section. Keep in sync with
  // `AuditLogFilterStateService.isAuditLogUrl`, which lists the same set to decide when to
  // discard the table's filters.
  readonly auditLogSectionActive = computed(() => {
    const path = this.activePath();
    return (
      path.startsWith('/activity-logs') ||
      path.startsWith('/disclosure-detail') ||
      path.startsWith('/admin-action-detail') ||
      path.startsWith('/appointment-detail')
    );
  });

  /** The verified account from the user endpoint; drives the header user menu. */
  readonly currentUser = this.authService.currentUser;

  readonly userName = computed(() => {
    const user = this.currentUser();
    return [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  });

  readonly userInitials = computed(() => {
    const user = this.currentUser();
    return [user?.firstName, user?.lastName]
      .map((part) => part?.trim()?.[0] ?? '')
      .join('')
      .toUpperCase();
  });

  constructor() {
    // Post-login landing is handled in AuthService.initialize(); App only keeps
    // the current URL in sync for the header/breadcrumb state.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroySubject$),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.userMenuOpen.set(false);
      });
  }

  setTheme(mode: ThemeMode) {
    this.themeService.setMode(mode);
  }

  toggleUserMenu(event: MouseEvent) {
    event.stopPropagation();
    this.userMenuOpen.update((open) => !open);
  }

  signOut() {
    this.userMenuOpen.set(false);
    this.authService.logout();
  }

  @HostListener('document:click')
  closeUserMenu() {
    if (this.userMenuOpen()) {
      this.userMenuOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.userMenuOpen.set(false);
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
